import { Component } from '@theme/component';
import { fetchConfig, debounce, onAnimationEnd, prefersReducedMotion, resetShimmer } from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

  connectedCallback() {
    super.connectedCallback();
    this.#updateRecommendation();
    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
    const lineItemRow = this.refs.cartItemRows[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (lineItemRow.querySelector('text-component'));
    textComponent?.shimmer();
  }
  // #scheduleRecommendationUpdate() {
  //   clearTimeout(this._recommendationTimeout);

  //   this._recommendationTimeout = setTimeout(() => {
  //     requestAnimationFrame(() => {
  //       requestAnimationFrame(() => {
  //         this.#updateRecommendation();
  //       });
  //     });
  //   }, 10000);
  // }
  async #updateRecommendation() {
    const container = document.querySelector(
      '#cart-recommendation'
    );
    
    if (!container) return;

    const productId =
      container.dataset.firstProductId;

    if (!productId) {
      container.remove();
      return;
    }

    try {
      const response = await fetch(
        `/recommendations/products.json?product_id=${productId}&limit=1&intent=related`
      );

      const data = await response.json();

      if (!data.products?.length) {
        container.remove();
        return;
      }

      const product = data.products[0];

      container.innerHTML =
        window.renderCartRecommendation(
          product
        );

      window.bindCartRecommendation?.(
        container,
        product
      );

    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });

    const cartItemRowToRemove = this.refs.cartItemRows[line - 1];

    if (!cartItemRowToRemove) return;

    const rowsToRemove = [
      cartItemRowToRemove,
      // Get all nested lines of the row to remove
      ...this.refs.cartItemRows.filter((row) => row.dataset.parentKey === cartItemRowToRemove.dataset.key),
    ];

    // Add class to the row to trigger the animation
    rowsToRemove.forEach((row) => {
      const remove = () => row.remove();

      if (prefersReducedMotion()) return remove();

      row.style.setProperty('--row-height', `${row.clientHeight}px`);
      row.classList.add('removing');

      // Remove the row after the animation ends
      onAnimationEnd(row, remove);
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: line,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(','),
      sections_url: window.location.pathname,
    });

    cartTotal?.shimmer();

    fetch(`${Theme.routes.cart_change_url}`, fetchConfig('json', { body }))
      .then((response) => {
        return response.text();
      })
      .then((responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(line, parsedResponseText);
          return;
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );

        morphSection(
          this.sectionId,
          parsedResponseText.sections[this.sectionId]
        );

        this.#updateRecommendation();

        this.#updateCartQuantitySelectorButtonStates();
      })
      .catch((error) => {
        console.error(error);
      })
      .finally(() => {
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (line, parsedResponseText) => {
    const quantitySelector = this.refs.quantitySelectors[line - 1];
    const quantityInput = quantitySelector?.querySelector('input');

    if (!quantityInput) throw new Error('Quantity input not found');

    quantityInput.value = quantityInput.defaultValue;

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement)) throw new Error('Cart item error not found');
    if (!(cartItemErrorContainer instanceof HTMLElement)) throw new Error('Cart item error container not found');

    cartItemError.textContent = parsedResponseText.errors;
    cartItemErrorContainer.classList.remove('hidden');
  };

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event instanceof DiscountUpdateEvent) {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
      return;
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    if (cartItemsHtml) {
      morphSection(
        this.sectionId,
        cartItemsHtml
      );

      this.#updateRecommendation();

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    } else {
      sectionRenderer.renderSection(this.sectionId, { cache: false });
    }
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}

function formatMoney(cents) {
  return new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',
  }).format(cents / 100);
}

window.renderCartRecommendation = function (product) {
  const imageUrl =
    product.featured_image?.startsWith('//')
      ? `https:${product.featured_image}`
      : product.featured_image;

  return `
    <div class="cart-recommendation__inner">
      <h3 class="cart-recommendation__heading">
        ${Theme.translations?.may_also_like || 'You may also like'}
      </h3>

      <div class="cart-recommendation__content">
        <div class="cart-recommendation__image">
          <img src="${imageUrl}" alt="${product.title}">
        </div>

        <div class="cart-recommendation__details">
          <a
            class="cart-recommendation__title"
            href="${product.url}"
          >
            ${product.title}
          </a>

          <div
            class="cart-recommendation__selected-options"
          ></div>

          <div
            class="cart-recommendation__variant-groups"
          ></div>
        </div>

        <div class="cart-recommendation__purchase">
          <div class="cart-recommendation__price-wrap">
            <span class="cart-recommendation__price">
              ${formatMoney(product.price)}
            </span>
          </div>

          <button
            type="button"
            class="cart-recommendation__atc"
          >
            Aggiungi
          </button>
        </div>
      </div>
    </div>
  `;
};

window.bindCartRecommendation = function (
  container,
  product
) {
  let selectedVariant =
    product.variants[0];

    const selectedOptionsContainer = container.querySelector('.cart-recommendation__selected-options');
    function renderSelectedOptions() {
      if (!selectedOptionsContainer)
        return;

      let html = '';

      product.options?.forEach(
        (option, index) => {

          const optionValue =
            selectedVariant[
              `option${index + 1}`
            ];

          if (!optionValue) return;

          html += `
            <div class="cart-rec-selected-option">
              <p>${option.name}:</p>
              <span>${optionValue}</span>
            </div>
          `;
        }
      );

      selectedOptionsContainer.innerHTML = html;
    }
    renderSelectedOptions();
  const variantsContainer =
    container.querySelector(
      '.cart-recommendation__variant-groups'
    );

  if (
    variantsContainer &&
    product.variants?.length > 1
  ) {
    const optionName =
      product.options?.[0]?.name || '';

    const isColor =
      optionName.toLowerCase().includes('color') ||
      optionName.toLowerCase().includes('colour') ||
      optionName.toLowerCase().includes('colore');

    const wrap =
      document.createElement('div');

    wrap.className =
      'cart-rec-option__values';

    product.variants
      .slice(0, 4)
      .forEach((variant, index) => {
        const btn =
          document.createElement('button');

        btn.type = 'button';

        btn.className =
          'cart-rec-option__button';

        if (index === 0) {
          btn.classList.add('is-active');
        }

        if (isColor) {
          const image =
            variant.featured_image?.src || '';

          btn.innerHTML = `
          <img
            src="${image}"
            alt="${variant.option1}"
          >
        `;
        } else {
          btn.textContent =
            variant.option1;
        }

        btn.addEventListener(
          'click',
          () => {
            wrap
              .querySelectorAll('.is-active')
              .forEach(el =>
                el.classList.remove(
                  'is-active'
                )
              );

            btn.classList.add(
              'is-active'
            );

            selectedVariant = variant;
            renderSelectedOptions();
          }
        );

        wrap.appendChild(btn);
      });

    if (product.variants.length > 4) {
      const more =
        document.createElement('span');

      more.textContent =
        '+' +
        (product.variants.length - 4);

      wrap.appendChild(more);
    }

    variantsContainer.appendChild(
      wrap
    );
  }

  const atc =
    container.querySelector(
      '.cart-recommendation__atc'
    );

  if (!atc) return;

  atc.addEventListener(
    'click',
    async () => {
      try {

        const formData = new FormData();

        formData.append(
          'id',
          selectedVariant.id
        );

        formData.append(
          'quantity',
          '1'
        );

        const cartItemsComponents =
          document.querySelectorAll(
            'cart-items-component'
          );

        const sectionIds = [];

        cartItemsComponents.forEach(
          (item) => {
            if (
              item instanceof HTMLElement &&
              item.dataset.sectionId
            ) {
              sectionIds.push(
                item.dataset.sectionId
              );
            }
          }
        );

        formData.append(
          'sections',
          sectionIds.join(',')
        );

        const response = await fetch(
          Theme.routes.cart_add_url,
          {
            method: 'POST',
            body: formData,
            headers: {
              Accept: 'application/json',
            },
          }
        );

        const result =
          await response.json();

        document.dispatchEvent(
          new CartAddEvent(
            {},
            selectedVariant.id.toString(),
            {
              source:
                'cart-recommendation',
              itemCount: 1,
              productId:
                product.id,
              sections:
                result.sections,
            }
          )
        );

      } catch (error) {
        console.error(error);
      }
    }
  );
};